/**
 * Photo layout editor.
 *
 * One product, several layouts. The customer uploads a photograph and picks a
 * layout; the layout is a full-poster overlay whose picture area is
 * transparent, so the photograph sits behind it and the type stays on white.
 *
 * The photograph is uploaded to the same Worker the photo-frame customizer
 * uses. Both the photo URL and the chosen layout ride to the cart, because the
 * print needs both and neither can be inferred from the other.
 */
/**
 * The add-to-cart form. A product page carries several `form[action*="/cart/add"]`
 * elements (Shop Pay installments, etc.); only the one with the add button
 * actually submits, so line-item properties must be written there.
 */
function cartForm() {
  const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
  return forms.find((f) => f.querySelector('button[name="add"]')) || forms[0] || null;
}

if (!customElements.get('photo-layout-editor')) {
  customElements.define(
    'photo-layout-editor',
    class PhotoLayoutEditor extends HTMLElement {
      connectedCallback() {
        this.poster = this.querySelector('[data-poster]');
        this.overlayEl = this.querySelector('[data-overlay]');
        this.grid = this.querySelector('[data-layouts-grid]');
        this.dropzone = this.querySelector('[data-dropzone]');
        this.fileInput = this.querySelector('[data-file]');
        this.thumb = this.querySelector('[data-thumb]');
        this.prompt = this.querySelector('[data-prompt]');
        this.clearBtn = this.querySelector('[data-clear]');
        this.empty = this.querySelector('[data-empty]');
        this.notice = this.querySelector('[data-notice]');

        this.endpoint = this.dataset.uploadEndpoint || '';
        this.inset = parseFloat(this.dataset.photoInset);
        this.layouts = JSON.parse(this.dataset.layouts || '[]');
        this.layoutIndex = 0;
        this.photo = null;
        this.frameEl = this.querySelector('[data-frame]');
        this.frameOverlay = this.dataset.frame === 'true';
        this.frameColors = null;
        this.openings = new Map();

        this.applyInset();
        this.buildLayoutGrid();
        this.applyLayout();
        this.bindUpload();
        this.gateSubmit();
        this.initFrame();
      }

      /** The margin is a share of the poster's width, so it stays even. */
      applyInset() {
        const pct = Number.isFinite(this.inset) ? this.inset : 5;
        this.style.setProperty('--photo-inset', `${pct}cqw`);
      }

      /* ------------------------------------------------------------ layout */

      buildLayoutGrid() {
        if (!this.grid) return;
        this.grid.innerHTML = '';
        this.layouts.forEach((layout, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'photo-layout__layout';
          btn.dataset.layoutIndex = String(i);
          btn.setAttribute('aria-label', layout.label || `Layout ${i + 1}`);
          btn.innerHTML = `
            <span class="photo-layout__layout-thumb" style="background-image:url('${layout.overlay}')"></span>
            <span class="photo-layout__layout-name"></span>`;
          btn.querySelector('.photo-layout__layout-name').textContent = layout.label || '';
          btn.addEventListener('click', () => {
            this.layoutIndex = i;
            this.applyLayout();
            this.syncProperties();
            if (this.photo && this.endpoint) this.upload(this.photo.file);
          });
          this.grid.appendChild(btn);
        });
      }

      applyLayout() {
        const layout = this.layouts[this.layoutIndex];
        if (layout && this.overlayEl) this.overlayEl.src = layout.overlay;
        this.grid?.querySelectorAll('[data-layout-index]').forEach((el) => {
          el.classList.toggle('is-active', Number(el.dataset.layoutIndex) === this.layoutIndex);
        });
        this.positionPhoto();
      }

      /* -------------------------------------------------------------- photo */

      bindUpload() {
        if (!this.dropzone || !this.fileInput) return;

        this.dropzone.addEventListener('click', (e) => {
          if (e.target.closest('[data-clear]')) return;
          this.fileInput.click();
        });

        this.fileInput.addEventListener('change', () => {
          const file = this.fileInput.files && this.fileInput.files[0];
          if (file) this.setPhoto(file);
        });

        ['dragenter', 'dragover'].forEach((type) =>
          this.dropzone.addEventListener(type, (e) => {
            e.preventDefault();
            this.dropzone.classList.add('is-dragging');
          })
        );
        ['dragleave', 'drop'].forEach((type) =>
          this.dropzone.addEventListener(type, (e) => {
            e.preventDefault();
            this.dropzone.classList.remove('is-dragging');
          })
        );
        this.dropzone.addEventListener('drop', (e) => {
          const file = e.dataTransfer?.files?.[0];
          if (file && file.type.startsWith('image/')) this.setPhoto(file);
        });

        this.clearBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.clearPhoto();
        });
      }

      setPhoto(file) {
        if (this.photo) URL.revokeObjectURL(this.photo.preview);
        this.photo = {
          file,
          preview: URL.createObjectURL(file),
          url: '',
          status: this.endpoint ? 'uploading' : 'ready',
        };

        this.photoEl().style.backgroundImage = `url('${this.photo.preview}')`;
        this.photoEl().classList.add('is-filled');
        if (this.empty) this.empty.hidden = true;
        if (this.thumb) {
          this.thumb.src = this.photo.preview;
          this.thumb.hidden = false;
        }
        if (this.prompt) this.prompt.hidden = true;
        if (this.clearBtn) this.clearBtn.hidden = false;
        this.hideNotice();
        this.updateStatus();

        if (this.endpoint) this.upload(file);
        else this.syncProperties();
      }

      photoEl() {
        return this.querySelector('[data-photo]');
      }

      async upload(file) {
        const kind = this.dataset.kind || 'custom-frame';
        // Keep the original photograph alongside the composite, so the source
        // is always recoverable from R2.
        this.postUpload(file, `${kind}-source`, file.name || 'source.png').catch(() => {});

        let blob = file;
        try {
          const composite = await this.composePoster();
          if (composite) blob = composite;
        } catch (error) {
          console.error('photo-layout-editor: compose failed', error);
        }
        try {
          const url = await this.postUpload(blob, kind, 'artwork.png');
          if (!this.photo) return;
          this.photo.url = url;
          this.photo.status = 'ready';
        } catch (error) {
          if (!this.photo) return;
          this.photo.status = 'error';
          this.photo.error = error.message || 'upload failed';
          console.error('photo-layout-editor: upload failed', error);
        }
        this.updateStatus();
        this.syncProperties();
      }

      async postUpload(blob, kind, filename) {
        const body = new FormData();
        body.append('file', blob, filename);
        body.append('layout', this.layouts[this.layoutIndex]?.label || '');
        body.append('product', this.dataset.product || '');
        body.append('kind', kind);
        const response = await fetch(`${this.endpoint}/upload`, { method: 'POST', body });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `upload failed (${response.status})`);
        return data.url;
      }

      updateStatus() {
        if (!this.dropzone) return;
        const status = this.photo?.status;
        this.dropzone.classList.toggle('is-uploading', status === 'uploading');
        this.dropzone.classList.toggle('is-error', status === 'error');
        if (this.prompt) {
          if (status === 'uploading') this.prompt.textContent = 'Uploading…';
          else if (status === 'error') this.prompt.textContent = `Upload failed — ${this.photo.error}`;
          else if (this.photo) this.prompt.textContent = this.photo.file.name;
        }
      }

      clearPhoto() {
        if (this.photo) URL.revokeObjectURL(this.photo.preview);
        this.photo = null;
        const el = this.photoEl();
        el.style.backgroundImage = '';
        el.classList.remove('is-filled');
        this.dropzone?.classList.remove('is-uploading', 'is-error', 'is-dragging');
        if (this.empty) this.empty.hidden = false;
        if (this.thumb) {
          this.thumb.removeAttribute('src');
          this.thumb.hidden = true;
        }
        if (this.prompt) {
          this.prompt.textContent = 'Choose a photo';
          this.prompt.hidden = false;
        }
        if (this.clearBtn) this.clearBtn.hidden = true;
        this.syncProperties();
      }

      /* ----------------------------------------------------------- the cart */

      /**
       * Both facts go to the cart. The print cannot be produced from the
       * photograph alone — it also needs to know which layout to print it in.
       */
      syncProperties() {
        const form = cartForm();
        if (!form) return;
        if (this.dataset.enableProperties !== 'true' && !this.endpoint) return;

        const write = (name, value) => {
          let input = form.querySelector(`input[name="${name}"]`);
          if (!value) {
            input?.remove();
            return;
          }
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.appendChild(input);
          }
          input.value = value;
        };

        write('properties[Artwork 1]', this.photo ? this.photo.url || this.photo.file.name : '');
        write('properties[Layout]', this.layouts[this.layoutIndex]?.label || '');
      }

      gateSubmit() {
        if (!this.endpoint) return;
        const form = cartForm();
        if (!form) return;
        form.addEventListener(
          'submit',
          (event) => {
            if (!this.photo || this.photo.status === 'ready') return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showNotice(
              this.photo.status === 'uploading'
                ? 'Hold on — your photo is still uploading. Give it a moment and try again.'
                : 'Your photo failed to upload. Remove and re-add it, then try again.'
            );
          },
          true
        );
      }

      showNotice(message) {
        if (!this.notice) return;
        this.notice.textContent = message;
        this.notice.hidden = false;
      }

      hideNotice() {
        if (this.notice) this.notice.hidden = true;
      }

      /* -------------------------------------------------------------- frame */

      /**
       * The frame colour is the product's own Color option, so the preview
       * follows the variant picker. Colours come from photo-frame-sizes.json,
       * the same table the frame customiser reads. The Pinterest Picks Frame
       * names that option Overlay, so an Overlay option is read the same way.
       */
      initFrame() {
        if (!this.frameOverlay || !this.frameEl) return;
        const url = this.dataset.sizes;
        if (url) {
          fetch(url)
            .then((r) => r.json())
            .then((data) => {
              this.frameColors = data.colors || {};
              this.applyFrame();
            })
            .catch(() => {});
        }
        document.addEventListener('change', (event) => {
          const target = event.target;
          if (!target || !target.matches) return;
          if (target.matches('input[name^="Frame"], select[name^="Frame"], input[name^="Color"], select[name^="Color"]')) {
            this.applyFrame();
          }
          if (target.matches('input[name^="Overlay"], select[name^="Overlay"]')) {
            this.matchOverlayLayout();
          }
        });
        window.addEventListener('resize', () => this.applyFrame());
        this.applyFrame();
        this.matchOverlayLayout();
      }

      /** The moulding follows the Frame option (or the older Color option). */
      frameColor() {
        const radio = [...document.querySelectorAll('input[type="radio"]')]
          .find((r) => /^(Frame|Color)/.test(r.name) && r.checked);
        if (radio) return radio.value;
        const select = document.querySelector('select[name^="Frame"], select[name^="Color"]');
        return select ? select.value : 'Black';
      }

      /** The mountboard *is* the layout, so the Overlay option picks the layout. */
      matchOverlayLayout() {
        const radio = [...document.querySelectorAll('input[type="radio"]')]
          .find((r) => /^Overlay/.test(r.name) && r.checked);
        const select = document.querySelector('select[name^="Overlay"]');
        const name = radio ? radio.value : (select ? select.value : null);
        if (!name || !this.layouts || !this.layouts.length) return;
        const i = this.layouts.findIndex(
          (l) => (l.label || '').toLowerCase() === String(name).toLowerCase()
        );
        if (i >= 0 && i !== this.layoutIndex) {
          this.layoutIndex = i;
          this.applyLayout();
          this.syncProperties();
        }
      }

      applyFrame() {
        if (!this.frameOverlay || !this.frameEl) return;
        const name = this.frameColor();
        const key = name === 'Golden' ? 'Gold' : name;
        const hex = (this.frameColors && this.frameColors[key] && this.frameColors[key].frame) || null;
        const poster = this.querySelector('[data-poster]');
        const width = poster ? poster.getBoundingClientRect().width : 0;
        const mould = hex ? Math.max(6, Math.round(width * 0.05)) : 0;
        this.frameEl.style.setProperty('--frame-w', `${mould}px`);
        this.frameEl.style.setProperty('--frame-color', hex || 'transparent');
        this.frameEl.classList.toggle('has-frame', Boolean(hex));
      }

      /* ------------------------------------------------------------ compose */

      /** Load an image for canvas work: same-origin or CORS-clean. */
      normalizeSrc(url) {
        try {
          const parsed = new URL(url, document.baseURI);
          // Assets served from the store/CDN over http taint the canvas; the
          // https response is CORS-enabled, so upgrade off-origin http.
          if (parsed.protocol === 'http:' && parsed.host !== location.host) parsed.protocol = 'https:';
          return parsed.href;
        } catch {
          return url;
        }
      }

      loadImage(url) {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = this.normalizeSrc(url);
        });
      }

      /** The overlay's transparent opening, as fractions of the overlay. */
      async openingFor(url) {
        if (this.openings.has(url)) return this.openings.get(url);
        let img;
        try {
          img = await this.loadImage(url);
        } catch {
          return null;
        }
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        let data;
        try {
          data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        } catch {
          return null; // cross-origin overlay — can't read the opening
        }
        let minX = canvas.width, minY = canvas.height, maxX = -1, maxY = -1;
        for (let y = 0; y < canvas.height; y += 2) {
          for (let x = 0; x < canvas.width; x += 2) {
            if (data[(y * canvas.width + x) * 4 + 3] < 10) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX < 0) return null;
        const frac = {
          x: minX / canvas.width,
          y: minY / canvas.height,
          w: (maxX - minX) / canvas.width,
          h: (maxY - minY) / canvas.height,
        };
        this.openings.set(url, frac);
        return frac;
      }

      /** Place the photo element inside the overlay's opening. */
      async positionPhoto() {
        if (!this.overlayEl) return;
        const el = this.photoEl();
        const opening = await this.openingFor(this.overlayEl.src);
        if (!el || !opening) return;
        el.classList.add('is-mount');
        el.style.inset = 'auto';
        el.style.top = `${opening.y * 100}%`;
        el.style.left = `${opening.x * 100}%`;
        el.style.width = `${opening.w * 100}%`;
        el.style.height = `${opening.h * 100}%`;
      }

      /**
       * Flatten the poster — the photograph, whole and fit inside the opening,
       * with the mountboard overlay on top — to a PNG for the print.
       */
      async composePoster() {
        if (!this.overlayEl || !this.photo) return null;
        const overlay = await this.loadImage(this.overlayEl.src);
        const w = overlay.naturalWidth;
        const h = overlay.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);

        const open = (await this.openingFor(this.overlayEl.src)) || { x: 0, y: 0, w: 1, h: 1 };
        const ox = open.x * w, oy = open.y * h, ow = open.w * w, oh = open.h * h;
        const photo = await this.loadImage(this.photo.preview);

        // Fit the photograph to the opening: whole when the aspect matches,
        // otherwise fill it (crop the overflow) so there are no blank bars.
        const cover = Math.max(ow / photo.naturalWidth, oh / photo.naturalHeight);
        const contain = Math.min(ow / photo.naturalWidth, oh / photo.naturalHeight);
        const sameAspect = Math.abs(photo.naturalWidth / photo.naturalHeight - ow / oh) < (ow / oh) * 0.02;
        const scale = sameAspect ? contain : cover;
        const dw = photo.naturalWidth * scale;
        const dh = photo.naturalHeight * scale;
        ctx.drawImage(photo, ox + (ow - dw) / 2, oy + (oh - dh) / 2, dw, dh);

        ctx.drawImage(overlay, 0, 0, w, h);
        return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      }
    }
  );
}
